<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { EquipmentRentalCategoryBadge } from '../../../../EquipmentRental/helpers/equipmentRentalCategoryBadges';
import type { EquipmentRentalAgreementOption } from '../../../../EquipmentRental/types/equipmentRental.types';
import type { FosterEquipmentRentalLineCheckoutPayload } from '../../../services/completeFosterManualCheckout.service';
import { useADialog, useAToast } from '@Atoms';
import { ADOPT_URL, getShelterNow } from '@Constants';
import { getPayloadDateTimeString } from '@Helpers';
import { useSessionStore } from '@Stores';
import { GetStartFosterReasonsService, StartFosterService } from '@AnimalProfileModule';
import { getFosterContractsService } from '@FosterModule';
import {
  AddFosterEquipmentRentalLinesService,
  CompleteFosterManualCheckoutService,
  DeleteCartSessionService,
  GetWaiversService,
  RemoveFosterEquipmentRentalLineService,
  SendCheckoutLinkService,
  fosterCartLinePetId,
} from '@ShoppingCartModule';
import { EQUIPMENT_RENTAL_NO_AGREEMENT_ID } from '../../../../EquipmentRental/constants/equipmentRental.constants';
import {
  resolveDefaultRentalAgreementId,
  shouldApplyDefaultRentalAgreement,
} from '../../../../EquipmentRental/helpers/equipmentRentalAgreementDefaults';
import { mapCategoryBadgesFromApi } from '../../../../EquipmentRental/helpers/equipmentRentalCategoryBadges';
import {
  formatIsoDateForDisplay,
  getTodayIsoDate,
  toIsoDateString,
} from '../../../../EquipmentRental/helpers/equipmentRentalDate.helpers';
import {
  getEquipmentRentalInventoryService,
  getEquipmentRentalRentalAgreementsService,
} from '../../../../EquipmentRental/services';
import useCartStore from '../../../store';
import OFosterMobileCheckoutDialog from '../OShoppingCartStepFosterCheckout/OFosterMobileCheckoutDialog.vue';

interface InventoryRow {
  id: string;
  equipmentTypeId: string;
  equipmentName: string;
  equipment_id: string;
  equipmentLabel: string;
  serialNumber: string;
  categoryIds: string[];
  categoryLabel: string;
  categoryBadges: EquipmentRentalCategoryBadge[];
  statusId: string;
}

interface CartLineRow {
  line_id?: string;
  equipment_type_id?: string;
  equipment_name?: string;
  equipment_id?: string;
  equipment_label?: string;
  category_label?: string;
}

interface InventoryGroupResponse {
  id?: string;
  type_name?: string;
  category_ids?: string[];
  category_label?: string;
  rows?: Array<Record<string, unknown>>;
}

interface InventoryTaxonomyOption {
  label?: string;
  value?: string;
  name?: string;
  color_hex?: string;
}

interface LineEdit {
  rental_agreement_id: string;
  rental_start_date: Date | undefined;
  due_date: Date | undefined;
  rental_notes: string;
}

const cartStore = useCartStore();
const { cartData, cartId, loading: cartLoading, pendingOutcomeRoute, people } = storeToRefs(cartStore);
const { outcomeCompletedCartReset, resetCart } = cartStore;
const { shelterProfile } = storeToRefs(useSessionStore());
const { activeDialog, activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const { t } = useI18n();
const { push } = useRouter();

const FOSTER_CATEGORY_NAME = 'foster_equipment';
const AVAILABLE_STATUS_NAME = 'available';

const adding = ref<Record<string, boolean>>({});
// Hex resolved from inventory `category_options` / `status_options`; empty until first fetch.
const fosterCategoryId = ref('');
const availableStatusId = ref('');
const agreements = ref<EquipmentRentalAgreementOption[]>([]);
const agreementsLoading = ref(true);
const animalOverrides = ref<Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }>>({
  ...cartStore.fosterCheckoutShared.animalOverrides,
});
const completeCheckoutLoading = ref(false);
const fosterContractId = ref<string | null>(cartStore.fosterCheckoutShared.fosterContractId ?? null);
const fosterContractOptions = ref<{ label: string; value: string }[]>([]);
const fosterStartDate = ref<Date>(cartStore.fosterCheckoutShared.fosterStartDate ?? getShelterNow());
const inventoryLoading = ref(false);
const inventoryRows = ref<InventoryRow[]>([]);
const lineEdits = ref<Record<string, LineEdit>>({});
const mobileCheckoutModalVisible = ref(false);
const mobileLinkLoading = ref(false);
const searchQuery = ref('');
const startFosterReasons = ref<any[]>([]);
const waiversOptions = ref<any[]>([]);

const adopterAppLink = computed(() => `${ADOPT_URL}/mobile-checkout/${cartId.value}`);
const animals = computed(() => cartData.value?.animals ?? []);
const colAction = computed(() => t('cart.stepper.fosterEquipmentRental.columns.action'));
const colDueDate = computed(() => t('cart.stepper.fosterEquipmentRental.columns.dueDate'));
const colEquipment = computed(() => t('equipmentRental.inventory.columns.equipmentType'));
const colEquipmentLabel = computed(() => t('equipmentRental.inventory.columns.equipment_label'));
const colSerialNumber = computed(() => t('equipmentRental.inventory.columns.serial_number'));
const colStartDate = computed(() => t('cart.stepper.fosterEquipmentRental.columns.startDate'));
const defaultAgreementId = computed(() => resolveDefaultRentalAgreementId(agreements.value));
const fosterContractsEnabled = computed(() => !!shelterProfile.value?.foster_waivers_enabled);
const fosterWaiversEnabled = computed(
  () => fosterContractsEnabled.value && !!shelterProfile.value?.enable_outcome_waivers
);

const equipmentLines = computed<CartLineRow[]>(() => {
  const raw = cartData.value?.equipment_lines;
  return Array.isArray(raw) ? raw : [];
});

// We only count cart lines that resolve to a foster-category inventory unit. Stale lines from a
// previous session — equipment types/units that have since been deleted or no longer fall under the
// Foster category — would otherwise keep the user trapped on the mobile/manual checkout path even
// though the table shows nothing in cart. The "Skip and Complete Foster Checkout" footer relies on
// this so an empty-looking table reliably gives the user the skip path.
const hasEquipmentInCart = computed(() => {
  if (fosterCategoryId.value === '') return false;

  for (const row of inventoryRows.value) {
    if (!row.categoryIds.includes(fosterCategoryId.value)) continue;
    if (cartLinesByUnit.value.has(equipmentUnitKey(row.equipmentTypeId, row.equipment_id))) return true;
  }
  return false;
});

/** Whether the shelter has any foster-category equipment in inventory at all. Drives the label on
 *  the single-action footer button: shelters with no foster equipment configured get the plain
 *  "Complete foster checkout" (nothing to skip), shelters that have equipment but the staff didn't
 *  add any get "Skip and Complete Foster Checkout" (skip = chose not to rent). */
const hasFosterInventoryAvailable = computed(() => {
  if (fosterCategoryId.value === '') return false;
  return inventoryRows.value.some((row) => row.categoryIds.includes(fosterCategoryId.value));
});

/** True when EVERY cart line is checked out as "No agreement required" (or its lineEdit hasn't been
 *  set, in which case it inherits the default; if the shelter's default IS no-agreement, it counts).
 *  The footer collapses to "Complete foster checkout" in that case — the rentals still get created
 *  via the manual handler, but there's nothing to sign so the mobile-checkout dance is pointless. */
const allCartLinesAreNoAgreement = computed(() => {
  if (!equipmentLines.value.length) return false;
  return equipmentLines.value.every((line) => {
    const lineId = String(line.line_id ?? '');
    const edit = lineEdits.value[lineId];
    const agreementId = edit?.rental_agreement_id ?? defaultAgreementId.value;
    return !agreementId || agreementId === EQUIPMENT_RENTAL_NO_AGREEMENT_ID;
  });
});

const agreementDropdownOptions = computed((): EquipmentRentalAgreementOption[] => {
  const none: EquipmentRentalAgreementOption = {
    _id: EQUIPMENT_RENTAL_NO_AGREEMENT_ID,
    title: t('equipmentRental.startRental.step2.noAgreementTitle'),
    body: '',
    is_default: false,
  };
  return [...agreements.value, none];
});

const cartLinesByUnit = computed(() => {
  const map = new Map<string, CartLineRow>();
  for (const line of equipmentLines.value) {
    if (!line.equipment_type_id && !line.equipment_id) continue;

    map.set(equipmentUnitKey(line.equipment_type_id ?? '', line.equipment_id ?? ''), line);
  }
  return map;
});

const filteredInventory = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  return inventoryRows.value
    .filter((row) => fosterCategoryId.value !== '' && row.categoryIds.includes(fosterCategoryId.value))
    .filter((row) => {
      if (availableStatusId.value !== '' && row.statusId === availableStatusId.value) return true;

      return cartLinesByUnit.value.has(equipmentUnitKey(row.equipmentTypeId, row.equipment_id));
    })
    .filter((row) => {
      if (!query) return true;

      return (
        row.equipmentName.toLowerCase().includes(query) ||
        row.equipmentLabel.toLowerCase().includes(query) ||
        row.serialNumber.toLowerCase().includes(query) ||
        row.categoryLabel.toLowerCase().includes(query)
      );
    });
});

/**
 * Three-way discriminator for the footer button(s). Driven by:
 *  - `fosterContractsEnabled` (shelter has Foster Contract & Waivers on?)
 *  - `hasEquipmentInCart` (staff added foster-category lines to the cart?)
 *  - `allCartLinesAreNoAgreement` (every line is "no agreement required"?)
 *
 *  - `mobile-manual` → render Continue/Skip-and-Continue with Mobile + Manual buttons. Used when
 *    something downstream needs a signing ceremony: foster contract on, OR there's a real rental
 *    agreement to sign.
 *  - `complete-with-rental` → render a single "Complete foster checkout" that routes through
 *    `onManualFosterCheckout` (which still creates the rentals via CompleteFosterManualCheckoutService),
 *    but the label and lack of a mobile option reflects that nothing needs signing — every line is
 *    "no agreement required".
 *  - `complete-only` → render the plain "Complete / Skip and Complete Foster Checkout" button that
 *    just starts the foster (no equipment processing). Cart is empty.
 */
const footerMode = computed<'mobile-manual' | 'complete-with-rental' | 'complete-only'>(() => {
  if (fosterContractsEnabled.value) return 'mobile-manual';
  if (hasEquipmentInCart.value && !allCartLinesAreNoAgreement.value) return 'mobile-manual';
  if (hasEquipmentInCart.value) return 'complete-with-rental';
  return 'complete-only';
});

/** Continue / Skip-and-Continue label for the mobile button. "Skip" appears only when there's
 *  foster equipment the staff could have added but didn't — empty inventory means "nothing to
 *  skip", populated inventory + empty cart means "you chose not to rent". */
const mobileCheckoutLabel = computed(() => {
  if (hasEquipmentInCart.value) return t('cart.label.continueMobileCheckout');
  if (hasFosterInventoryAvailable.value) return t('cart.stepper.fosterEquipmentRental.skipAndContinueMobileCheckout');
  return t('cart.label.continueMobileCheckout');
});

const manualCheckoutLabel = computed(() => {
  if (hasEquipmentInCart.value) return t('cart.label.continueManualCheckout');
  if (hasFosterInventoryAvailable.value) return t('cart.stepper.fosterEquipmentRental.skipAndContinueManualCheckout');
  return t('cart.label.continueManualCheckout');
});

/** Label for the single "Complete foster checkout" button when no equipment is in cart. "Skip"
 *  only fires when foster equipment was actually available — otherwise there's nothing to skip. */
const completeOnlyLabel = computed(() =>
  hasFosterInventoryAvailable.value
    ? t('cart.stepper.fosterEquipmentRental.skipAndCompleteFosterCheckout')
    : t('cart.label.completeFosterCheckout')
);

const tableColumns = computed(() => [
  colEquipment.value,
  colEquipmentLabel.value,
  colSerialNumber.value,
  colStartDate.value,
  colDueDate.value,
  colAction.value,
]);

const tableRows = computed(() =>
  filteredInventory.value.map((row) => ({
    id: row.id,
    [colEquipment.value]: row.equipmentName,
    [colEquipmentLabel.value]: row.equipmentLabel,
    [colSerialNumber.value]: row.serialNumber || '—',
    [colStartDate.value]: '',
    [colDueDate.value]: '',
    [colAction.value]: '',
    _inventoryRow: row,
  }))
);

const waiverOptionsWithId = computed(() =>
  waiversOptions.value
    .map((waiver) => ({
      ...waiver,
      id: String(waiver._id?.$oid ?? waiver._id ?? ''),
      title: waiver.title ?? waiver.name ?? waiver.label,
    }))
    .filter((waiver) => waiver.id !== '')
);

watch(() => cartStore.fosterCheckoutShared.fosterStartDate, onFosterStartDateChange);
watch(() => cartStore.fosterCheckoutShared.fosterContractId, onFosterContractIdChange);
watch(() => cartStore.fosterCheckoutShared.animalOverrides, onAnimalOverridesChange, { deep: true });
watch([equipmentLines, defaultAgreementId], syncFosterLineEdits, { immediate: true });
watch(mobileCheckoutModalVisible, onMobileCheckoutModalVisibleChange);

onMounted(onMount);

async function onMount() {
  await Promise.all([fetchInventory(), fetchAgreements(), preloadFosterCheckoutContext()]);
}

function onFosterStartDateChange(next: Date | null) {
  if (!(next instanceof Date) || next.getTime() === fosterStartDate.value.getTime()) return;

  fosterStartDate.value = next;
}

function onFosterContractIdChange(next: string | null) {
  fosterContractId.value = next;
}

function onAnimalOverridesChange(
  next: Record<string, { waiver_id?: string; waiver_ids?: string[]; notes?: string }> | undefined
) {
  animalOverrides.value = { ...(next ?? {}) };
}

function syncFosterLineEdits() {
  const defaultId = defaultAgreementId.value;
  for (const line of equipmentLines.value) {
    const lineId = String(line.line_id ?? '');
    if (!lineId) continue;

    const existing = lineEdits.value[lineId];
    if (!existing) {
      lineEdits.value[lineId] = {
        rental_agreement_id: defaultId,
        rental_start_date: getShelterNow(),
        due_date: undefined,
        rental_notes: '',
      };
      continue;
    }

    if (shouldApplyDefaultRentalAgreement(existing.rental_agreement_id, defaultId)) {
      lineEdits.value[lineId] = {
        ...existing,
        rental_agreement_id: defaultId,
      };
    }
  }
}

function onMobileCheckoutModalVisibleChange(isOpen: boolean) {
  if (isOpen) return;

  mobileLinkLoading.value = false;
}

function normalizeCategoryIds(group: InventoryGroupResponse): string[] {
  return Array.isArray(group.category_ids) ? group.category_ids.map((id) => String(id)).filter(Boolean) : [];
}

function mapInventoryPayloadToRows(groups: unknown): InventoryRow[] {
  const groupList = Array.isArray(groups) ? (groups as InventoryGroupResponse[]) : [];
  const rows: InventoryRow[] = [];

  for (const group of groupList) {
    const groupCategoryIds = normalizeCategoryIds(group);
    for (const row of group.rows ?? []) {
      rows.push({
        id: String((row as Record<string, unknown>)._id ?? row.id ?? ''),
        equipmentTypeId: String(row.equipment_type_id ?? ''),
        equipmentName: String(row.equipment_name ?? group.type_name ?? ''),
        equipment_id: String(row.equipment_id ?? ''),
        equipmentLabel: String((row as Record<string, unknown>).equipment_label ?? ''),
        serialNumber: String((row as Record<string, unknown>).serial_number ?? ''),
        categoryIds: groupCategoryIds,
        categoryLabel: String(row.category_label ?? group.category_label ?? ''),
        categoryBadges: mapCategoryBadgesFromApi(
          (row as Record<string, unknown>).category_badges ?? (group as Record<string, unknown>).category_badges
        ),
        statusId: String((row as Record<string, unknown>).status_id ?? ''),
      });
    }
  }

  return rows;
}

function resolveOptionHexByName(options: unknown, slug: string): string {
  if (!Array.isArray(options)) return '';

  const match = (options as InventoryTaxonomyOption[]).find(
    (option) => String(option?.name ?? '').toLowerCase() === slug
  );
  return String(match?.value ?? '');
}

function fetchInventory() {
  inventoryLoading.value = true;

  const TOAST_ERROR = {
    type: TOAST_TYPES.ERROR,
    title: t('common.error'),
    message: t('cart.toast.equipmentRental.loadInventoryError'),
  };

  return getEquipmentRentalInventoryService()
    .then((payload) => {
      inventoryRows.value = mapInventoryPayloadToRows(payload?.groups);
      fosterCategoryId.value = resolveOptionHexByName(payload?.category_options, FOSTER_CATEGORY_NAME);
      availableStatusId.value = resolveOptionHexByName(payload?.status_options, AVAILABLE_STATUS_NAME);
    })
    .catch(() => {
      inventoryRows.value = [];
      notify(TOAST_ERROR);
    })
    .finally(() => (inventoryLoading.value = false));
}

function fetchAgreements() {
  agreementsLoading.value = true;

  return getEquipmentRentalRentalAgreementsService()
    .then((list) => {
      agreements.value = list;
      syncFosterLineEdits();
    })
    .catch(() => (agreements.value = []))
    .finally(() => (agreementsLoading.value = false));
}

async function preloadFosterCheckoutContext() {
  const reasonsResponse = await GetStartFosterReasonsService();
  const reasonsList = Array.isArray(reasonsResponse) ? reasonsResponse : (reasonsResponse as any)?.data;
  startFosterReasons.value = Array.isArray(reasonsList) ? reasonsList : [];

  if (fosterContractsEnabled.value && !waiversOptions.value.length) {
    const waiversResponse = await GetWaiversService('outcome', 'foster_checkout');
    waiversOptions.value = waiversResponse?.waivers ?? (Array.isArray(waiversResponse) ? waiversResponse : []);
  }

  return getFosterContractsService()
    .then((raw) => {
      const contracts = Array.isArray(raw) ? raw : [];
      fosterContractOptions.value = contracts
        .map((contract: any) => ({
          label: contract.title ?? contract.name ?? '',
          value: contract._id?.$oid ?? contract._id ?? '',
        }))
        .filter((option: { value: string }) => option.value !== '');

      if (!fosterContractId.value) {
        const defaultContract: any = shelterProfile.value?.default_foster_contract_id;
        const defaultOid = defaultContract?.$oid ?? defaultContract ?? '';
        const match = contracts.find((contract: any) => (contract._id?.$oid ?? contract._id ?? '') === defaultOid);
        const firstId = contracts[0] ? (contracts[0]._id?.$oid ?? contracts[0]._id ?? '') : '';
        fosterContractId.value = match ? defaultOid : firstId || null;
      }
    })
    .catch(() => (fosterContractOptions.value = []));
}

// NOTE: equipment_id is user-defined, so we key the "already in cart" lookup by JSON-encoding the
// [type, unit] pair — collision-proof for any characters, no magic separator.
function equipmentUnitKey(equipmentTypeId: string, equipmentId: string): string {
  return JSON.stringify([equipmentTypeId, equipmentId]);
}

function isRowInCart(row: InventoryRow): boolean {
  return cartLinesByUnit.value.has(equipmentUnitKey(row.equipmentTypeId, row.equipment_id));
}

function cartLineForRow(row: InventoryRow): CartLineRow | undefined {
  return cartLinesByUnit.value.get(equipmentUnitKey(row.equipmentTypeId, row.equipment_id));
}

function lineEditFor(row: InventoryRow) {
  const line = cartLineForRow(row);
  const lineId = String(line?.line_id ?? '');
  return lineEdits.value[lineId];
}

function openAddDialog(row: InventoryRow) {
  activeModalProps.value = {
    row: {
      id: row.id,
      equipmentName: row.equipmentName,
      equipment_id: row.equipment_id,
      equipmentLabel: row.equipmentLabel,
      serialNumber: row.serialNumber,
    },
    agreementOptions: agreementDropdownOptions.value,
    defaultAgreementId: defaultAgreementId.value,
    onConfirm: (start: Date, due: Date | undefined, agreementId: string) =>
      addLineFromDialog(row, start, due, agreementId),
  };
  activeModal.value = SYSTEM_MODALS.CART_FOSTER_EQUIPMENT_RENTAL_DATES;
  dialogVisibility.value = true;
}

function addLineFromDialog(
  row: InventoryRow,
  chosenStart: Date,
  chosenDue: Date | undefined,
  chosenAgreementId: string
): Promise<void> {
  adding.value[row.id] = true;
  return Promise.resolve(
    AddFosterEquipmentRentalLinesService({
      equipment_lines: [
        {
          equipment_type_id: row.equipmentTypeId,
          equipment_id: row.equipment_id,
          equipment_label: row.equipmentLabel,
          serial_number: row.serialNumber,
          equipment_name: row.equipmentName,
          category_label: row.categoryLabel,
          category_badges: row.categoryBadges.map((badge) => ({
            _id: badge._id,
            label: badge.label,
            color_hex: badge.colorHex,
          })),
        },
      ],
    })
  )
    .then((result: any) => {
      const updatedCart = (result?.data ?? result) as any;
      cartData.value = {
        ...cartData.value,
        equipment_lines: Array.isArray(updatedCart?.equipment_lines) ? updatedCart.equipment_lines : [],
      };
      const lines: any[] = Array.isArray(updatedCart?.equipment_lines) ? updatedCart.equipment_lines : [];
      const newLine = lines.find(
        (line) =>
          String(line.equipment_type_id ?? '') === row.equipmentTypeId &&
          String(line.equipment_id ?? '') === row.equipment_id
      );
      const lineId = String(newLine?.line_id ?? '');
      if (lineId) {
        lineEdits.value[lineId] = {
          rental_agreement_id: chosenAgreementId || EQUIPMENT_RENTAL_NO_AGREEMENT_ID,
          rental_start_date: chosenStart,
          due_date: chosenDue,
          rental_notes: '',
        };
      }
    })
    .finally(() => (adding.value[row.id] = false));
}

function onRemoveRow(row: InventoryRow) {
  const line = cartLineForRow(row);
  const lineId = String(line?.line_id ?? '');
  if (!lineId) return;

  cartLoading.value = true;

  const TOAST_ERROR = {
    type: TOAST_TYPES.ERROR,
    title: t('cart.toast.removeItem.error.title'),
    message: t('cart.toast.removeItem.error.message'),
  };

  Promise.resolve(RemoveFosterEquipmentRentalLineService(lineId))
    .then((result: any) => {
      const updatedCart = (result?.data ?? result) as any;
      cartData.value = {
        ...cartData.value,
        equipment_lines: Array.isArray(updatedCart?.equipment_lines) ? updatedCart.equipment_lines : [],
      };
      delete lineEdits.value[lineId];
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (cartLoading.value = false));
}

function collectWaiverIdsFromList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];

  const ids: string[] = [];
  for (const entry of list) {
    if (entry == null) continue;

    if (typeof entry === 'string') {
      ids.push(entry);
      continue;
    }

    const oid = (entry as any)?._id?.$oid ?? (entry as any)?._id ?? (entry as any)?.id?.$oid ?? (entry as any)?.id;
    if (oid != null && oid !== '') ids.push(String(oid));
  }
  return ids;
}

function getWaiverIdsForAnimal(animal: any): string[] | undefined {
  if (!fosterWaiversEnabled.value) return undefined;

  const allowed = new Set(waiverOptionsWithId.value.map((waiver) => waiver.id));
  const id = fosterCartLinePetId(animal);
  const override = id !== '' ? animalOverrides.value[id] : undefined;
  if (override != null && Object.prototype.hasOwnProperty.call(override, 'waiver_ids')) {
    const filtered = (override.waiver_ids ?? []).filter((waiver) => allowed.has(String(waiver)));
    return filtered.length ? filtered : undefined;
  }

  if (override?.waiver_id && allowed.has(String(override.waiver_id))) return [override.waiver_id];

  const fromAnimal = [
    ...new Set([...collectWaiverIdsFromList(animal.intake_waivers), ...collectWaiverIdsFromList(animal.waivers)]),
  ].filter((waiver) => allowed.has(String(waiver)));
  if (fromAnimal.length) return fromAnimal;

  return undefined;
}

function getFosterAnimalsWaiversRowForPayload(animal: any) {
  const id = fosterCartLinePetId(animal);
  const rawIds = getWaiverIdsForAnimal(animal);
  const notesText = id !== '' ? (animalOverrides.value[id]?.notes ?? '') : '';
  const reasonId = animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id;
  return {
    animal_id: id,
    waiver_ids: Array.isArray(rawIds) ? rawIds.map((waiver) => String(waiver)) : [],
    reserve_kennel_spot: !!animal.reserve_kennel_spot,
    start_date: getPayloadDateTimeString(fosterStartDate.value) ?? '',
    ...(notesText ? { start_foster_notes: notesText } : {}),
    ...(animal.start_foster_reason ? { start_foster_reason: animal.start_foster_reason } : {}),
    ...(reasonId ? { start_foster_reason_id: String(reasonId) } : {}),
  };
}

function buildStartFosterPayload(
  animal: any,
  startDateString: string,
  person: any,
  PAYLOAD?: { cart_id: string; contract_id: string }
) {
  const id = fosterCartLinePetId(animal);
  const notesText = id !== '' ? (animalOverrides.value[id]?.notes ?? '') : '';
  const reasonId = animal.start_foster_reason_id?.$oid || animal.start_foster_reason_id;
  const waiverIdsRaw = getWaiverIdsForAnimal(animal);
  return {
    intake_id: animal.intake_id?.$oid || animal.intake_id,
    parent_id: animal.parent_id?.$oid || animal.parent_id || person?._id?.$oid || person?._id || '',
    parent_name: animal.parent_name || person?.full_name || '',
    pet_id: id || animal.animal_id?.$oid || animal.animal_id,
    start_date: startDateString,
    reserve_kennel_spot: !!animal.reserve_kennel_spot,
    ...(notesText ? { start_foster_notes: notesText } : {}),
    ...(animal.start_foster_reason ? { start_foster_reason: animal.start_foster_reason } : {}),
    ...(reasonId ? { start_foster_reason_id: reasonId } : {}),
    ...(waiverIdsRaw?.length ? { waiver_ids: waiverIdsRaw } : {}),
    ...(PAYLOAD
      ? {
          foster_manual_checkout_cart_id: PAYLOAD.cart_id,
          foster_manual_checkout_contract_id: PAYLOAD.contract_id,
        }
      : {}),
  } as any;
}

function runStartFosterForAllAnimals(PAYLOAD?: { cart_id: string; contract_id: string }) {
  // Date AND time, matching step one. formatNumberToDate() defaults includeTime to false, so this
  // used to drop the time the user picked and start the foster at midnight.
  const startDateString = getPayloadDateTimeString(fosterStartDate.value) ?? '';
  const person = Array.isArray(cartData.value?.people) ? cartData.value?.people[0] : cartData.value?.people;
  return animals.value.reduce(
    (chain: Promise<unknown>, animal: any) =>
      chain.then(() => StartFosterService(buildStartFosterPayload(animal, startDateString, person, PAYLOAD))),
    Promise.resolve() as Promise<unknown>
  );
}

function agreementMeta(id: string): EquipmentRentalAgreementOption | undefined {
  if (id === EQUIPMENT_RENTAL_NO_AGREEMENT_ID) return undefined;

  return agreements.value.find((agreement) => agreement._id === id);
}

function buildEquipmentRentalLinesForPayload(): FosterEquipmentRentalLineCheckoutPayload[] {
  return equipmentLines.value.map((line) => {
    const lineId = String(line.line_id ?? '');
    const edit: LineEdit = lineEdits.value[lineId] ?? {
      rental_agreement_id: defaultAgreementId.value,
      rental_start_date: getShelterNow(),
      due_date: undefined,
      rental_notes: '',
    };
    const lineAgreementId = edit.rental_agreement_id || EQUIPMENT_RENTAL_NO_AGREEMENT_ID;
    const meta = agreementMeta(lineAgreementId);
    return {
      line_id: lineId,
      rental_agreement_id:
        lineAgreementId === EQUIPMENT_RENTAL_NO_AGREEMENT_ID ? '__equipment_rental_no_agreement__' : lineAgreementId,
      rental_start_date: toIsoDateString(edit.rental_start_date) || getTodayIsoDate(),
      due_date: toIsoDateString(edit.due_date),
      rental_notes: edit.rental_notes || '',
      agreement_title: meta?.title ?? 'Rental agreement',
      agreement_body: meta?.body ?? '',
    };
  });
}

function validateForm(): boolean {
  if (!fosterStartDate.value) {
    const TOAST_START_DATE_REQUIRED = {
      type: TOAST_TYPES.ERROR,
      title: t('common.error'),
      message: t('cart.label.fosterStartDate'),
    };
    notify(TOAST_START_DATE_REQUIRED);

    return false;
  }

  if (fosterContractsEnabled.value && !fosterContractId.value) {
    const TOAST_CONTRACT_REQUIRED = {
      type: TOAST_TYPES.ERROR,
      title: t('common.error'),
      message: t('cart.label.fosterContract'),
    };
    notify(TOAST_CONTRACT_REQUIRED);

    return false;
  }

  return true;
}

function onCancelFoster() {
  activeDialog.value = SYSTEM_MODALS.CART_EMPTY_DIALOG;
  dialogVisibility.value = true;
}

function onContinueMobileCheckout() {
  if (!validateForm()) return;

  mobileCheckoutModalVisible.value = true;
}

function onFosterMobileCheckoutDialogSubmit(detail: {
  checkoutFlow: string;
  deviceCheckoutMethod: string;
  email: string;
  phone: string;
}) {
  mobileLinkLoading.value = true;
  let email = detail.email;
  const phone = detail.checkoutFlow === 'adopter' ? detail.phone : '';
  const isShelterLinkCheckout = detail.checkoutFlow === 'shelter' && detail.deviceCheckoutMethod === 'link';
  if (detail.checkoutFlow !== 'adopter' && !isShelterLinkCheckout) {
    email = '';
  }

  const PAYLOAD: Record<string, unknown> = {
    _id: cartId.value,
    cart_id: cartId.value,
    checkoutFlow: detail.checkoutFlow,
    email,
    payment_type: null,
    phone,
    show_application: null,
    show_contract: null,
    status: 'pending',
  };
  if (fosterContractsEnabled.value && fosterContractId.value) {
    PAYLOAD.foster_contract_id = String(fosterContractId.value);
  }

  if (cartData.value?.cart_type === 'foster') {
    PAYLOAD.foster_animals_waivers = animals.value.map(getFosterAnimalsWaiversRowForPayload);
  }

  if (equipmentLines.value.length) {
    PAYLOAD.equipment_rental_lines = buildEquipmentRentalLinesForPayload();
  }

  const TOAST_ERROR = {
    message: 'errors.generics.unknownIssue.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  SendCheckoutLinkService(PAYLOAD as any)
    .then(() => {
      if (detail.deviceCheckoutMethod === 'newTab') window.open(adopterAppLink.value, '_blank');
      mobileCheckoutModalVisible.value = false;
      return push(pendingOutcomeRoute.value!).then(() => outcomeCompletedCartReset());
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (mobileLinkLoading.value = false));
}

function onCompleteFosterCheckout() {
  if (!validateForm()) return;

  const TOAST_ERROR = {
    title: 'errors.generics.unknownIssue.title',
    message: 'errors.generics.unknownIssue.message',
    type: TOAST_TYPES.ERROR,
  };
  const TOAST_SUCCESS = {
    title: 'cart.toast.fosterCheckoutComplete.title',
    message: 'cart.toast.fosterCheckoutComplete.message',
    type: TOAST_TYPES.SUCCESS,
  };
  completeCheckoutLoading.value = true;
  runStartFosterForAllAnimals()
    .then(() => DeleteCartSessionService(false))
    .then(() => {
      resetCart();
      notify(TOAST_SUCCESS);
      return push({ name: 'animals-in-foster' });
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (completeCheckoutLoading.value = false));
}

function onManualFosterCheckout() {
  if (!validateForm()) return;

  const rawCartId = cartData.value?.cart_id?.$oid || cartData.value?.cart_id;
  // Mirror the mobile-checkout submit guard: a stale `fosterContractId` left in the cart store
  // (auto-populated from the shelter's default_foster_contract_id, or a prior session where the
  // flag was on) must NOT leak into the payload when the shelter currently has Foster Contract &
  // Waivers disabled. Without this, the manual path would ship a real contract hex even though
  // staff never saw or picked a contract — and the completion view's print menu would then surface
  // "Foster Contract" + "All Documents" on what should be a rental-only foster cart.
  const contractId = fosterContractsEnabled.value ? fosterContractId.value : null;
  if (!rawCartId) return;

  const TOAST_ERROR = {
    title: 'errors.generics.unknownIssue.title',
    message: 'errors.generics.unknownIssue.message',
    type: TOAST_TYPES.ERROR,
  };
  const TOAST_SUCCESS = {
    title: 'cart.toast.fosterCheckoutComplete.title',
    message: 'cart.toast.fosterCheckoutComplete.message',
    type: TOAST_TYPES.SUCCESS,
  };

  completeCheckoutLoading.value = true;
  const PAYLOAD = contractId ? { cart_id: String(rawCartId), contract_id: String(contractId) } : undefined;

  runStartFosterForAllAnimals(PAYLOAD)
    .then(() => {
      if (contractId) {
        return CompleteFosterManualCheckoutService({
          cart_id: rawCartId,
          foster_contract_id: contractId,
          ...(cartData.value?.cart_type === 'foster'
            ? { foster_animals_waivers: animals.value.map(getFosterAnimalsWaiversRowForPayload) }
            : {}),
          ...(equipmentLines.value.length ? { equipment_rental_lines: buildEquipmentRentalLinesForPayload() } : {}),
        });
      }

      if (equipmentLines.value.length) {
        return CompleteFosterManualCheckoutService({
          cart_id: rawCartId,
          foster_contract_id: '',
          equipment_rental_lines: buildEquipmentRentalLinesForPayload(),
        });
      }

      return DeleteCartSessionService(false);
    })
    .then(() => {
      resetCart();
      notify(TOAST_SUCCESS);
      return push({ name: 'foster-payment-completed', params: { id: rawCartId } });
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (completeCheckoutLoading.value = false));
}

function formatDateForRowDisplay(value: Date | null | undefined): string {
  const out = formatIsoDateForDisplay(toIsoDateString(value));
  return out === '' ? '—' : out;
}
</script>

<template>
  <div class="flex max-w-[1440px] flex-col gap-6">
    <section class="flex flex-col gap-3">
      <h4 class="font-bold text-gray-700">
        {{ $t('cart.stepper.fosterEquipmentRental.pickerHeading') }}
      </h4>

      <p class="text-sm text-slate-500">
        {{ $t('cart.stepper.fosterEquipmentRental.pickerHelp') }}
      </p>

      <span class="relative block w-full md:max-w-md">
        <i class="pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />

        <InputText
          v-model="searchQuery"
          class="w-full pl-10"
          type="search"
          :placeholder="$t('cart.stepper.fosterEquipmentRental.searchPlaceholder')"
        />
      </span>

      <p
        v-if="inventoryLoading"
        class="py-3 text-center text-sm text-slate-500"
      >
        {{ $t('common.loading') }}…
      </p>

      <p
        v-else-if="!tableRows.length"
        class="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-slate-500"
      >
        {{ $t('cart.stepper.fosterEquipmentRental.noInventory') }}
      </p>

      <MShelterTable
        v-else
        usePagination
        :columns="tableColumns"
        :rows="tableRows"
        :rowsPerPage="10"
      >
        <template #default="{ row, column }">
          <template v-if="column === colStartDate">
            <span
              v-if="isRowInCart(row._inventoryRow)"
              class="text-slate-700"
            >
              {{ formatDateForRowDisplay(lineEditFor(row._inventoryRow)?.rental_start_date) }}
            </span>

            <span
              v-else
              class="text-slate-400"
            >
              —
            </span>
          </template>

          <template v-else-if="column === colDueDate">
            <span
              v-if="isRowInCart(row._inventoryRow) && lineEditFor(row._inventoryRow)?.due_date"
              class="text-slate-700"
            >
              {{ formatDateForRowDisplay(lineEditFor(row._inventoryRow)?.due_date) }}
            </span>

            <span
              v-else-if="isRowInCart(row._inventoryRow)"
              class="italic text-slate-400"
            >
              {{ $t('equipmentRental.inventory.dueDateModal.notSet') }}
            </span>

            <span
              v-else
              class="text-slate-400"
            >
              —
            </span>
          </template>

          <template v-else-if="column === colAction">
            <Button
              v-if="!isRowInCart(row._inventoryRow)"
              class="af-button af-button-success !text-xs"
              icon="pi pi-plus"
              :label="$t('cart.stepper.fosterEquipmentRental.addRow')"
              :loading="!!adding[row._inventoryRow.id]"
              @click="openAddDialog(row._inventoryRow)"
            />

            <Button
              v-else
              text
              class="af-button !text-xs"
              icon="pi pi-trash"
              severity="danger"
              :label="$t('common.remove')"
              @click="onRemoveRow(row._inventoryRow)"
            />
          </template>

          <span v-else>
            {{ row[column] || '—' }}
          </span>
        </template>
      </MShelterTable>
    </section>

    <div class="flex flex-wrap items-center justify-between gap-3 pt-2">
      <Button
        class="af-button w-fit"
        icon="pi pi-trash"
        severity="danger"
        :label="$t('cart.label.cancelFoster')"
        @click="onCancelFoster"
      />

      <!--
        Footer is a three-way fork driven by `footerMode`:

         - 'mobile-manual': there's a signing ceremony to run (foster contract is on, OR at least
            one cart line carries a real rental agreement). Render Mobile + Manual buttons; labels
            flip between "Continue with…" and "Skip and Continue with…" depending on whether the
            staff actually added equipment.

         - 'complete-with-rental': contracts are off AND every cart line is "no agreement required".
            Nothing to sign, but rentals still need to be created (inventory flip + rental doc).
            Route through `onManualFosterCheckout` — it calls CompleteFosterManualCheckoutService
            with `foster_contract_id: ''` so neither the foster contract PDF nor any signature is
            generated, but the rentals materialise. Label says "Complete Foster Checkout" because
            from the staff's POV they're done.

         - 'complete-only': cart is empty. Just kick off the start-foster pipeline via
            `onCompleteFosterCheckout`. Label is "Skip and Complete…" when foster equipment was
            actually offered (skip = staff chose not to rent) and "Complete…" otherwise (nothing
            to skip).
      -->
      <div
        v-if="footerMode === 'mobile-manual'"
        class="flex flex-wrap items-center gap-2"
      >
        <Button
          class="af-button af-button-success w-fit"
          icon="pi pi-mobile"
          :label="mobileCheckoutLabel"
          @click="onContinueMobileCheckout"
        />

        <Button
          class="af-button af-button-success w-fit"
          icon="pi pi-user"
          :label="manualCheckoutLabel"
          :loading="completeCheckoutLoading"
          @click="onManualFosterCheckout"
        />
      </div>

      <Button
        v-else-if="footerMode === 'complete-with-rental'"
        class="af-button af-button-success w-fit"
        icon="pi pi-check"
        :label="$t('cart.label.completeFosterCheckout')"
        :loading="completeCheckoutLoading"
        @click="onManualFosterCheckout"
      />

      <Button
        v-else
        class="af-button af-button-success w-fit"
        icon="pi pi-check"
        :label="completeOnlyLabel"
        :loading="completeCheckoutLoading"
        @click="onCompleteFosterCheckout"
      />
    </div>

    <OFosterMobileCheckoutDialog
      v-model:visible="mobileCheckoutModalVisible"
      :people
      :submitLoading="mobileLinkLoading"
      @onSubmit="onFosterMobileCheckoutDialogSubmit"
    />
  </div>
</template>
